import { useEffect, useState } from "react";
import type { MouseEvent } from "react";
import { ArrowRight, Circle, CircleCheck } from "lucide-react";
import { api } from "../api/client";
import { useNavModel } from "../nav/areas";
import { isPlainLeftClick } from "../nav/LinkAdapter";
import { useNavCountsContext } from "../nav/NavCountsContext";
import { card, theme, type as text, pageHeading, pageSubtitle } from "../theme";

// ── Guides ────────────────────────────────────────────────────────────────
// Step-by-step journeys through the pages the platform already has. Each step
// links to the page that completes it and is ticked from the organisation's
// own data (GET /nav/counts, GET /templates/adoption) - never from a box the
// user ticks, so a guide cannot say "done" about something that is not.

interface Step {
  label: string;
  detail: string;
  to: string;
  /** null = no signal to read (the step is guidance, not a check). */
  done: boolean | null;
}

interface Guide {
  id: string;
  title: string;
  intro: string;
  steps: Step[];
}

interface AdoptionSummary {
  generated: number;
  adopted: number;
  awaitingAdoption: number;
}

function StepRow({ step, index }: { step: Step; index: number }) {
  const { adapter } = useNavModel();
  const [hov, setHov] = useState(false);
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (!adapter || !isPlainLeftClick(e)) return;
    e.preventDefault();
    adapter.navigate(step.to);
  };
  return (
    <a
      href={adapter ? adapter.href(step.to) : step.to}
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 16px",
        borderTop: "1px solid var(--c-border)", textDecoration: "none",
        background: hov ? "var(--c-panelAlt)" : "transparent",
      }}
    >
      <span style={{ display: "flex", marginTop: 1, color: step.done ? "var(--c-success)" : "var(--c-faint)", flexShrink: 0 }}>
        {step.done ? <CircleCheck size={17} /> : <Circle size={17} />}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ ...text.itemTitle, display: "block", color: step.done ? theme.color.subtle : theme.color.heading, textDecoration: step.done ? "line-through" : "none" }}>
          {index + 1}. {step.label}
        </span>
        <span style={{ ...text.hint, display: "block", marginTop: 2 }}>
          {step.detail}
          {/* A step the platform cannot see done is said so, rather than left out of the count unexplained (AUD-47). */}
          {step.done === null && <span style={{ fontStyle: "italic" }}> Not ticked automatically.</span>}
        </span>
      </span>
      <ArrowRight size={14} style={{ color: "var(--c-faint)", marginTop: 3, flexShrink: 0 }} />
    </a>
  );
}

export function GuidesPage() {
  const { counts } = useNavCountsContext();
  const client = counts?.role === "client" ? counts : null;
  const [adoption, setAdoption] = useState<AdoptionSummary | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.get<{ policies: unknown[]; adopted: number; awaitingAdoption: number }>("/templates/adoption")
      .then((d) => { if (!cancelled) setAdoption({ generated: d.policies.length, adopted: d.adopted, awaitingAdoption: d.awaitingAdoption }); })
      .catch(() => { if (!cancelled) setAdoption(null); });
    return () => { cancelled = true; };
  }, []);

  const setup = client?.setup;
  const known = (v: boolean | undefined) => (v === undefined ? null : v);

  const guides: Guide[] = [
    {
      id: "first-policy",
      title: "Get your first policy adopted",
      intro: "A policy the platform writes is a draft. It counts once someone has read it, made it yours and put your copy on file.",
      steps: [
        { label: "Tell us about your business", detail: "Your answers tailor every policy to how you actually work.", to: "/compliance-questions", done: known(setup?.intake) },
        { label: "Write a policy", detail: "Pick one — the information security policy is a good first choice - and generate it.", to: "/templates", done: adoption ? adoption.generated > 0 : null },
        { label: "Review it and put your copy on file", detail: "Edit it until it is true of your organisation, then upload it from the policy's card.", to: "/templates", done: adoption ? adoption.adopted > 0 : null },
      ],
    },
    {
      id: "audit-ready",
      title: "Prepare for your audit",
      intro: "The same order an auditor will look at things in: what you are certifying, where you stand, and the evidence behind it.",
      steps: [
        { label: "Choose your standard", detail: "The standard you are being certified against.", to: "/my-services", done: known(setup?.frameworks) },
        { label: "Run a compliance check", detail: "The AI reads your evidence against every requirement and lists the gaps.", to: "/compliance", done: known(setup?.firstCheck) },
        { label: "Connect or upload your evidence", detail: "Connect Microsoft 365 or Google so evidence arrives on its own, or upload files.", to: "/connections", done: known(setup?.evidence) },
        { label: "Adopt your policies", detail: "Every draft policy reviewed and on file.", to: "/templates", done: adoption ? adoption.adopted > 0 && adoption.awaitingAdoption === 0 : null },
        { label: "Clear what's outstanding", detail: "Work through What's next until nothing urgent is left.", to: "/actions", done: client ? client.actions === 0 : null },
        { label: "Download your audit pack", detail: "Everything an auditor will ask for, in one download.", to: "/export", done: null },
      ],
    },
    {
      id: "incident",
      title: "Handle a security incident",
      intro: "If something has happened, start the record now: some regulators must be told within 24 hours of you becoming aware.",
      steps: [
        { label: "Log the incident", detail: "Say what happened and when you became aware — that time starts every clock.", to: "/resilience/incidents", done: null },
        { label: "See who must be told, and by when", detail: "The incident lists each regulator, insurer and authority the law in your locations requires, with its deadline.", to: "/resilience/incidents", done: null },
        { label: "Record each notification as you make it", detail: "Your reference numbers are the evidence you told them in time.", to: "/resilience/incidents", done: null },
      ],
    },
  ];

  return (
    <div style={{ padding: "20px 24px" }}>
      <h1 style={pageHeading}>Guides</h1>
      <p style={{ ...pageSubtitle, margin: "0 0 20px" }}>
        Step by step through the things most organisations need to do. Steps tick themselves off as you complete them.
      </p>
      {guides.map((guide) => {
        const checkable = guide.steps.filter((s) => s.done !== null);
        const done = checkable.filter((s) => s.done).length;
        return (
          <section key={guide.id} style={{ ...card, padding: 0, overflow: "hidden", marginBottom: 16 }}>
            <div style={{ padding: "14px 16px 12px" }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
                <h2 style={{ ...text.sectionTitle, margin: 0 }}>{guide.title}</h2>
                {checkable.length > 0 && (
                  <span style={{ ...text.hint, fontWeight: 600, color: done === checkable.length ? theme.color.successText : theme.color.subtle, flexShrink: 0 }}>
                    {done} of {checkable.length} {checkable.length === guide.steps.length ? "done" : "tracked steps done"}
                  </span>
                )}
              </div>
              <p style={{ ...text.hint, margin: "4px 0 0" }}>{guide.intro}</p>
            </div>
            {guide.steps.map((step, i) => <StepRow key={`${guide.id}-${i}`} step={step} index={i} />)}
          </section>
        );
      })}
    </div>
  );
}
